import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

export type EventCategory =
  | 'rapat_meeting'
  | 'workshop_pelatihan'
  | 'webinar_seminar'
  | 'sosialisasi_diskusi'
  | 'kegiatan_lainnya'

export interface CalendarEvent {
  id: string
  title: string
  description: string | null
  start_dt: string
  end_dt: string
  location: string | null
  category: EventCategory
  divisi: string | null
  is_external: boolean
  created_at: string
}

export interface NewCalendarEvent {
  title: string
  description?: string
  start_dt: string
  end_dt: string
  location?: string
  category: EventCategory
  divisi?: string
  is_external?: boolean
}

export const CATEGORY_META: Record<EventCategory, {
  label: string
  dot: string
  text: string
  bg: string
  border: string
}> = {
  rapat_meeting:       { label: 'Rapat / Meeting',      dot: 'bg-blue-500',   text: 'text-blue-600',   bg: 'bg-blue-50',   border: 'border-blue-200' },
  workshop_pelatihan:  { label: 'Workshop / Pelatihan',  dot: 'bg-purple-500', text: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200' },
  webinar_seminar:     { label: 'Webinar / Seminar',     dot: 'bg-amber-500',  text: 'text-amber-600',  bg: 'bg-amber-50',  border: 'border-amber-200' },
  sosialisasi_diskusi: { label: 'Sosialisasi / Diskusi', dot: 'bg-green-500',  text: 'text-green-600',  bg: 'bg-green-50',  border: 'border-green-200' },
  kegiatan_lainnya:    { label: 'Kegiatan Lainnya',      dot: 'bg-pink-500',   text: 'text-pink-600',   bg: 'bg-pink-50',   border: 'border-pink-200' },
}

export function formatEventTime(dt: string) {
  return new Intl.DateTimeFormat('id-ID', {
    hour: '2-digit', minute: '2-digit',
    timeZone: 'Asia/Jakarta', hour12: false,
  }).format(new Date(dt))
}

export function getWIBDateString(dt: string) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Jakarta',
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date(dt))
}

export function useCalendarEvents(year: number, month: number) {
  const [events, setEvents] = useState<CalendarEvent[]>([])
  const [loading, setLoading] = useState(true)

  const fetch = useCallback(() => {
    setLoading(true)
    // Fetch prev month start to next month end (covers all calendar days shown)
    const from = new Date(Date.UTC(year, month - 1, 1)).toISOString()
    const to   = new Date(Date.UTC(year, month + 2, 0, 23, 59, 59)).toISOString()
    supabase
      .from('calendar_events')
      .select('*')
      .gte('start_dt', from)
      .lte('start_dt', to)
      .order('start_dt', { ascending: true })
      .then(({ data }) => {
        setEvents(data ?? [])
        setLoading(false)
      })
  }, [year, month])

  useEffect(() => { fetch() }, [fetch])

  const addEvent = async (event: NewCalendarEvent) => {
    const { error } = await supabase.from('calendar_events').insert(event)
    if (!error) fetch()
    return { error }
  }

  return { events, loading, refetch: fetch, addEvent }
}

export function useUpcomingEvents(limit = 5) {
  const [events, setEvents] = useState<CalendarEvent[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase
      .from('calendar_events')
      .select('*')
      .gte('start_dt', new Date().toISOString())
      .order('start_dt', { ascending: true })
      .limit(limit)
      .then(({ data }) => {
        setEvents(data ?? [])
        setLoading(false)
      })
  }, [limit])

  return { events, loading }
}
